import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navigation() {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fade out between 0 and 120px of scroll
  const navOpacity = Math.max(1 - scrollY / 120, 0);
  const isVisible = navOpacity > 0;

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.treatments'), path: '/treatments' },
    { name: t('nav.howItWorks'), path: '/how-it-works' },
    { name: t('nav.supervision'), path: '/supervision' },
    { name: t('nav.about'), path: '/about' },
    { name: 'FAQ', path: '/faq' },
  ];

  return (
    <nav
      className="fixed left-0 right-0 z-50 transition-opacity duration-300"
      style={{ top: 'var(--banner-height, 40px)', opacity: navOpacity, pointerEvents: isVisible ? 'auto' : 'none' }}
    >
      {/* Solid black bar */}
      <div className="bg-black w-full">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-7 h-7 flex-shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <defs>
                  <linearGradient id="logoGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#d97706', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <circle cx="35" cy="50" r="25" fill="url(#logoGradient1)" opacity="0.9" />
                <circle cx="65" cy="50" r="25" fill="url(#logoGradient1)" opacity="0.9" />
                <circle cx="50" cy="50" r="15" fill="black" />
                <circle cx="50" cy="50" r="8" fill="url(#logoGradient1)" />
              </svg>
            </div>
            <span
              className="text-base font-semibold whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="text-white">Plexus</span>
              {' '}
              <span className="text-amber-400">Alliance</span>
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.path}
                className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher isScrolled={false} isServicePage={false} />
            <Link
              to="/book-treatment"
              className="px-5 py-2 text-sm font-semibold bg-amber-400 text-black hover:bg-amber-300 transition-all duration-200 whitespace-nowrap cursor-pointer tracking-wide"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Start Now
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center text-white cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden bg-black overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-[400px]' : 'max-h-0'
        }`}
      >
        <div className="px-6 pb-5 pt-2 space-y-3 border-t border-white/10">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-white/70 hover:text-white text-sm font-medium transition-colors duration-200 cursor-pointer py-1"
            >
              {link.name}
            </a>
          ))}
          <div className="pt-2 border-t border-white/10">
            <LanguageSwitcher isScrolled={true} isServicePage={false} />
          </div>
          <Link
            to="/book-treatment"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block w-full px-6 py-2.5 bg-amber-400 text-black text-center text-sm font-semibold hover:bg-amber-300 transition-all duration-200 cursor-pointer tracking-wide"
          >
            Start Now
          </Link>
        </div>
      </div>
    </nav>
  );
}
